/* @flow */

import type { BO_API_SETTING } from '../../../../libs/netgemLibrary/videofutur/types/Discovery';
import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { NetgemApiEmitterType, RequestResponseMethodDefinitionType } from '../emitter';

import { buildMissingParametersError, logError } from '../../../../helpers/debug/debug';
import { createCustomNetworkErrorFromKey } from '../../../../libs/netgemLibrary/helpers/CreateCustomNetworkError';
import { HttpMethod } from '../../../../libs/netgemLibrary/v8/types/HttpMethod';
import { REDUX_MSG_REQUEST_BO_DISCOVERY } from '../../constants';
import { getSettingValueByName } from '../helpers/settings';
import { addDistributor } from '../index';
import {
  type BOApiUrlDefinition,
  BO_API_ADD_FAVORITE,
  BO_API_CREATE_STREAM2,
  BO_API_DELETE_FAVORITE,
  BO_API_FAVORITE_LIST,
  BO_API_GET_STREAMS,
  BO_API_PERSONAL_DATA,
  BO_API_PURCHASE,
  BO_API_PURCHASE_LIST,
  BO_API_PURCHASE_STATUS,
  BO_API_START_STREAM,
  BO_API_STOP_STREAM,
  type Distributor,
} from './types/common';

const buildUrlDefinition: (settings: Array<BO_API_SETTING>, apiName: string, method: HttpMethod, queryStringParameters?: Array<string>) => BOApiUrlDefinition | null = (
  settings,
  apiName,
  method,
  queryStringParameters,
) => {
  const setting = settings.find((s) => s.type === apiName);
  if (!setting) {
    return null;
  }

  const { url } = setting;

  return {
    authent: true,
    method,
    params: queryStringParameters ?? [],
    uri: url,
  };
};

const sendDiscoveryRequestForDistributor: (distributor: string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (distributor, signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType): Promise<Distributor> => {
    const {
      appConfiguration,
      appConfiguration: {
        configuration: { boV2ServiceInfoUrl: ptfDiscoveryUrl },
      },
    } = getState();
    const baseurl = getSettingValueByName('platform.vf', 'baseurl', appConfiguration);
    const discoveryEndpoint = getSettingValueByName('platform.vf', 'discovery', appConfiguration);
    const device = getSettingValueByName('platform.vf', 'device', appConfiguration);

    // Overridden value from DMS
    const dmsDiscoveryUrl = getSettingValueByName('platform.vf', 'boV2ServiceInfoUrl', appConfiguration);

    // Check parameters
    if (!baseurl || !device || !discoveryEndpoint) {
      throw buildMissingParametersError(
        {
          baseurl,
          device,
          discoveryEndpoint,
        },
        'BO discovery',
      );
    }

    return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_BO_DISCOVERY, {
      device,
      discoveryUrl: dmsDiscoveryUrl ?? ptfDiscoveryUrl,
      distributor,
      signal,
    })
      .then((service) => {
        if (!service) {
          throw createCustomNetworkErrorFromKey('common.messages.errors.missing_url_definitions_for_distributor', { distributorId: distributor });
        }

        const { settings } = service;

        const distributorDef: Distributor = {
          urls: {
            [BO_API_ADD_FAVORITE]: buildUrlDefinition(settings, BO_API_ADD_FAVORITE, HttpMethod.POST),
            [BO_API_CREATE_STREAM2]: buildUrlDefinition(settings, BO_API_CREATE_STREAM2, HttpMethod.POST, ['vtiId']),
            [BO_API_DELETE_FAVORITE]: buildUrlDefinition(settings, BO_API_DELETE_FAVORITE, HttpMethod.DELETE),
            [BO_API_FAVORITE_LIST]: buildUrlDefinition(settings, BO_API_FAVORITE_LIST, HttpMethod.GET),
            [BO_API_GET_STREAMS]: buildUrlDefinition(settings, BO_API_GET_STREAMS, HttpMethod.GET),
            [BO_API_PERSONAL_DATA]: buildUrlDefinition(settings, BO_API_PERSONAL_DATA, HttpMethod.GET, []),
            [BO_API_PURCHASE]: buildUrlDefinition(settings, BO_API_PURCHASE, HttpMethod.POST, ['vtiId']),
            [BO_API_PURCHASE_LIST]: buildUrlDefinition(settings, BO_API_PURCHASE_LIST, HttpMethod.GET),
            [BO_API_PURCHASE_STATUS]: buildUrlDefinition(settings, BO_API_PURCHASE_STATUS, HttpMethod.POST, ['vtiId']),
            [BO_API_START_STREAM]: buildUrlDefinition(settings, BO_API_START_STREAM, HttpMethod.POST, ['streamId']),
            [BO_API_STOP_STREAM]: buildUrlDefinition(settings, BO_API_STOP_STREAM, HttpMethod.POST, ['streamId']),
          },
        };

        dispatch(addDistributor(distributor, distributorDef));
        return Promise.resolve(distributorDef);
      })
      .catch((error) => {
        logError(`Error retrieving discovery for distributor: ${distributor} | ${error.message}`);
        throw error;
      });
  };

export default sendDiscoveryRequestForDistributor;
